import { Badge, Banner, Button, ButtonGroup, Chip, Counter, IconButton, Menu, PanelHead, Spinner, TextArea, useEscapeLayer, type MenuEntry } from '@ui';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { fitHistory } from '../../../shared/askHistory';
import { redactText } from '../../../shared/redact';
import { ApiError } from '../api';
import type { QuestionSeed } from '../BugReportDialog';
import { bugApi, type QuestionPlace } from '../bugApi';
import { uxWatchOffered, whatsNewConfig, whatsNewWithoutPage, type LauncherConfig } from '../config';
import {
  BugIcon,
  ChatIcon,
  CheckIcon,
  ClickIcon,
  CloseIcon,
  CopyIcon,
  CropIcon,
  EyeIcon,
  FileCodeIcon,
  HelpIcon,
  ListIcon,
  PlusIcon,
  ScreensIcon,
  SendIcon,
  ShieldIcon,
  SparkIcon,
  UsersIcon,
} from '../icons';
import { PANEL_TEXT_ATTR } from '../capture';
import { BUG_OWN_ATTR, setPicking } from '../recorder';
import { isScreenMaskKey, onScreenMask, screenMaskOn, toggleScreenMask } from '../screenMask';
import { useHostSlot } from './hostSlot';
import { askCode, askService, hostRoute, type AnswerAudience, type AnswerDepth, type CodeAnswer } from './askApi';
import {
  areaTarget,
  elementTarget,
  excerpt,
  MAX_TARGETS,
  newTargetId,
  OWN,
  placesLabel,
  screenTarget,
  squash,
  withTarget,
  anchorsOf,
  visibleMarks,
  type Box,
  type Target,
  type TargetKind,
} from './targets';

/**
 * Help button in the bottom right corner, like support chats: "ask a question", "bug report", "watch me". A question is
 * about up to six places on the screen (optional): an element by click (Shift+click — one more, staying in the pick),
 * an area by frame, selected text ("ask" next to it; with the panel open — "to the question") and the whole screen. The
 * answer comes from the service by the tab's `appVersion` (`launcher.ask.service`; it stores the question itself, and
 * "no code of this version" marks a guess) or from the host (`launcher.ask.path`), in two kinds: "in the code" (proofs
 * matched; links are only copied) or "not found" with a guess. Host options: "Проще / Подробнее" under each answer (`depth`: the same question again,
 * the new answer replaces the old one), the "Продукт / Разработчик" toggle (`audience`, kept for the panel's next
 * questions; `modes` — both), previous questions sent with a follow-up (`history`), places' tooltips (`hints`).
 */

const OWN_PROPS = { [BUG_OWN_ATTR]: '' };
const OFF_TIP = 'Сервис баг-репортов не подключён';
const PICK_STYLE_ID = 'jaiora-bugs-pick';
const DEFAULT_SOURCE_ATTR = 'data-src';

const DEPTH: Record<AnswerDepth, { text: string; tip: string; wait: string }> = {
  simple: { text: 'Проще', tip: 'Ответить на этот вопрос проще и короче', wait: 'Переписываю проще' },
  detailed: { text: 'Подробнее', tip: 'Ответить на этот вопрос подробнее', wait: 'Переписываю подробнее' },
};
const DEPTHS: AnswerDepth[] = ['simple', 'detailed'];
const AUDIENCE: Record<AnswerAudience, { text: string; tip: string; next: AnswerAudience }> = {
  product: { text: 'Продукт', tip: 'Обычными словами, без названий из кода · нажмите — для разработчика', next: 'developer' },
  developer: { text: 'Разработчик', tip: 'С названиями из кода · нажмите — обычными словами', next: 'product' },
};

interface Turn {
  id: number;
  question: string;
  target: string;
  /** The places as asked: "Проще / Подробнее" asks again about them. */
  targets: Target[];
  /** The same places as stored — for the tracker and «Сделать репортом». */
  places: QuestionPlace[];
  depth: AnswerDepth;
  state: 'wait' | 'done' | 'error';
  answer?: CodeAnswer;
  error?: string;
  /** The answer is being asked again at this depth; the old one stays until the new one comes. */
  redo?: AnswerDepth;
  redoError?: string;
  /** In the tracker: its number; `failed` — the service did not take it (the answer still stands in the panel). */
  saved?: { state: 'saving' } | { state: 'saved'; id: number } | { state: 'failed' };
}

/* Report 106: the person's words and the page's text in this panel — covered by the screen mode's sheet, like the report panel's. */
const PANEL_TEXT = { [PANEL_TEXT_ATTR]: '' };
const answerText = (answer: CodeAnswer): string => (answer.found ? answer.answer : answer.guess ?? '');

/** The question with its places and answer (the one shown now) for «Сделать репортом». */
export function seedOf(turn: Turn): QuestionSeed | null {
  if (turn.state !== 'done' || !turn.answer) return null;
  return {
    id: turn.saved?.state === 'saved' ? turn.saved.id : null,
    question: turn.question,
    place: turn.target,
    places: turn.places,
    found: turn.answer.found,
    answer: answerText(turn.answer),
  };
}

const TARGET_ICON: Record<TargetKind, () => JSX.Element> = {
  element: ClickIcon,
  text: HelpIcon,
  area: CropIcon,
  screen: ScreensIcon,
};

/**
 * Crosshair over the whole host page while picking (the widget CSS is scoped, so this one rule goes in on its own); the
 * recorder treats the page as the widget's meanwhile.
 */
function pickCursor(on: boolean): void {
  setPicking(on);
  const old = document.getElementById(PICK_STYLE_ID);
  if (!on) {
    old?.remove();
    return;
  }
  if (old) return;
  const el = document.createElement('style');
  el.id = PICK_STYLE_ID;
  el.textContent = `html, html * { cursor: crosshair !important; }\nhtml ${OWN}, html ${OWN} * { cursor: auto !important; }\nhtml ${OWN} button, html ${OWN} button * { cursor: pointer !important; }`;
  document.head.appendChild(el);
}

/** Outline of an element or an area frame over everything; follows scrolling. */
function Outline({ el, box, tone, label }: { el?: Element | null; box?: Box; tone: 'pick' | 'chosen'; label?: string }) {
  const [rect, setRect] = useState<Box | null>(null);
  useEffect(() => {
    if (!el && !box) {
      setRect(null);
      return;
    }
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (box) setRect({ left: box.left - window.scrollX, top: box.top - window.scrollY, width: box.width, height: box.height });
        else if (el && el.isConnected) setRect(el.getBoundingClientRect());
        else setRect(null);
      });
    };
    measure();
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [el, box]);
  if (!rect) return null;
  return (
    <div className={`help-outline help-outline-${tone}`} style={{ top: rect.top - 3, left: rect.left - 3, width: rect.width + 6, height: rect.height + 6 }}>
      {label && <span className="help-outline-num">{label}</span>}
    </div>
  );
}

interface DepthProps {
  /** `ask.depth`: buttons under a ready answer. */
  on: boolean;
  /** A question is under way: the buttons wait. */
  busy: boolean;
  onPick: (depth: AnswerDepth) => void;
}

function AnswerView({ turn, depth, onReport }: { turn: Turn; depth: DepthProps; onReport?: () => void }) {
  const [copied, setCopied] = useState(false);
  if (turn.state === 'wait') {
    return (
      <div className="help-a help-a-wait">
        <Spinner size="sm" label="Ищу в коде" />
        <span>Ищу в коде</span>
      </div>
    );
  }
  if (turn.state === 'error' || !turn.answer) return <div className="help-a help-a-error">{turn.error ?? 'Не получилось'}</div>;
  const { answer } = turn;
  const copy = () => {
    const links = answer.proofs.map((proof) => `${proof.file}:${proof.line}`).join('\n');
    void navigator.clipboard?.writeText(links).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  };
  return (
    <div className="help-a">
      <div className="help-a-head">
        {answer.found ? (
          <Badge tone="success" icon={<CheckIcon />} className="help-a-found" title={`Подтверждено кодом: ${answer.proofs.length}`}>
            Есть в коде
          </Badge>
        ) : (
          <Badge tone="warning" icon={<HelpIcon />} className="help-a-found" title="В коде подтверждения не нашлось">
            Не найдено
          </Badge>
        )}
        {answer.snapshot === 'missing' && (
          <Badge icon={<FileCodeIcon />} className="help-a-nocode" title="У сервиса нет кода версии, открытой во вкладке, — ответ только «не нашёл в коде»">
            нет кода версии
          </Badge>
        )}
        {answer.found && (
          <IconButton size="sm" label={copied ? 'Скопировано' : 'Скопировать ссылки на код'} icon={copied ? <CheckIcon /> : <CopyIcon />} onClick={copy} />
        )}
        <span className="help-a-spacer" />
        {turn.saved?.state === 'saved' && (
          <Badge icon={<ListIcon />} className="help-a-saved" title="Вопрос и ответ сохранены в трекере">
            №{turn.saved.id}
          </Badge>
        )}
        {turn.saved?.state === 'failed' && (
          <Badge tone="error" icon={<ListIcon />} className="help-a-saved" title="Трекер не принял вопрос — ответ только в этом окне">
            не в трекере
          </Badge>
        )}
        {onReport && (
          <Button size="sm" variant="quiet" icon={<BugIcon />} title="Новый баг-репорт с этим местом и ответом" onClick={onReport}>
            Сделать репортом
          </Button>
        )}
      </div>
      {answer.found ? (
        <p className="help-a-text" {...PANEL_TEXT}>
          {answer.answer}
        </p>
      ) : (
        <p className="help-a-text" {...PANEL_TEXT}>
          <span className="help-a-guess">Не нашёл в коде:</span> {answer.guess ?? 'нет'}
        </p>
      )}
      {depth.on && (
        <div className="help-a-depth">
          {turn.redo ? (
            <span className="help-a-redo">
              <Spinner size="sm" label={DEPTH[turn.redo].wait} />
              {DEPTH[turn.redo].wait}
            </span>
          ) : (
            <ButtonGroup label="Ответ проще или подробнее">
              {DEPTHS.map((value) => (
                <Button
                  key={value}
                  size="sm"
                  variant="quiet"
                  title={value === turn.depth ? 'Так и ответил' : DEPTH[value].tip}
                  aria-pressed={value === turn.depth}
                  disabled={depth.busy || value === turn.depth}
                  onClick={() => depth.onPick(value)}
                >
                  {DEPTH[value].text}
                </Button>
              ))}
            </ButtonGroup>
          )}
          {turn.redoError && <span className="help-a-redo-error">{turn.redoError}</span>}
        </div>
      )}
    </div>
  );
}

/** Area frame: drag over the screen; Esc — out. */
function AreaFrame({ onDone, onCancel }: { onDone: (box: Box) => void; onCancel: () => void }) {
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [now, setNow] = useState<{ x: number; y: number } | null>(null);
  useEscapeLayer(true, onCancel);
  const box =
    start && now
      ? { left: Math.min(start.x, now.x), top: Math.min(start.y, now.y), width: Math.abs(now.x - start.x), height: Math.abs(now.y - start.y) }
      : null;
  const down = (event: ReactPointerEvent<HTMLDivElement>) => {
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // The pointer is already up — the frame still follows without capture.
    }
    setStart({ x: event.clientX, y: event.clientY });
    setNow({ x: event.clientX, y: event.clientY });
  };
  const move = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (start) setNow({ x: event.clientX, y: event.clientY });
  };
  const up = () => {
    if (box && box.width >= 8 && box.height >= 8) onDone(box);
    setStart(null);
    setNow(null);
  };
  return (
    <div className="help-area" onPointerDown={down} onPointerMove={move} onPointerUp={up}>
      {box && <div className="help-area-rect" style={box} />}
    </div>
  );
}

export interface HelpLauncherProps {
  launcher: LauncherConfig;
  /** The service lets this user in (`ready().enabled`). */
  bugsOn: boolean;
  /** The service lets this user see the list (`ready().isSuper`). */
  isSuper?: boolean;
  draft: boolean;
  minimized: boolean;
  uxWatching: boolean;
  onBugReport: () => void;
  /** «Сделать репортом» on an answer: a new report headed by the question's place and answer. */
  onReportFromQuestion?: (seeds: QuestionSeed[]) => void;
  onUxWatch: () => void;
  /** The menu is opening: the service is asked again if the items are grey (Сс1). */
  onMenuOpen?: () => void;
}

export default function HelpLauncher({
  launcher,
  bugsOn: serviceOn,
  isSuper = false,
  draft,
  minimized,
  uxWatching,
  onBugReport,
  onReportFromQuestion,
  onMenuOpen,
  onUxWatch,
}: HelpLauncherProps) {
  const ask = launcher.ask && (launcher.ask.service === true || typeof launcher.ask.path === 'string') ? launcher.ask : null;
  // The service answers (and stores) — without it there is no question, as there is no report (Quint facts code_questions П272).
  const viaService = ask?.service === true;
  const askLive = !viaService || serviceOn;
  const attr = ask?.sourceAttr || DEFAULT_SOURCE_ATTR;
  // The host said it cannot answer now (or the service is off): told at once, nothing is sent.
  const askOff = ask?.unavailable?.trim() || (askLive ? '' : OFF_TIP);
  const showBugs = launcher.bugs !== false;
  const bugsOn = showBugs && serviceOn;
  const openTracker = typeof launcher.openTracker === 'function' ? launcher.openTracker : null;
  // Super — the whole tracker; anyone else who can ask — their own questions on the same host page.
  const showTracker = !!openTracker && serviceOn && (isSuper || !!ask);
  // «Что нового» — the host's release notes on a screen of their own; the host's notes, so no service needed.
  const whatsNew = whatsNewConfig();
  const openWhatsNew = typeof whatsNew?.open === 'function' ? whatsNew.open : null;
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = menuAnchor !== null;
  const closeMenu = () => setMenuAnchor(null);
  const screenMaskOffered = launcher.screenMask === true || launcher.screenMask === 'default-on';
  const screenMasked = useSyncExternalStore(onScreenMask, screenMaskOn);
  // П610: the button in the host's frame while its node is on the page; otherwise in the corner.
  const slotRoot = useHostSlot(launcher.slot);
  const [mode, setMode] = useState<'idle' | 'pick' | 'area' | 'ask'>('idle');
  const [hover, setHover] = useState<Element | null>(null);
  const [targets, setTargets] = useState<Target[]>([]);
  const [selection, setSelection] = useState<{ target: Target; x: number; y: number } | null>(null);
  const [text, setText] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [audience, setAudience] = useState<AnswerAudience>('product');
  const depthOn = !!ask && (ask.depth === true || ask.modes === true);
  const audienceOn = !!ask && (ask.audience === true || ask.modes === true);
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const threadRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!screenMaskOffered) return;
    const onKey = (event: KeyboardEvent) => {
      if (!isScreenMaskKey(event)) return;
      event.preventDefault();
      toggleScreenMask();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [screenMaskOffered]);
  const nextTurn = useRef(1);
  const full = targets.length >= MAX_TARGETS;

  const focusInput = () => window.setTimeout(() => inputRef.current?.focus(), 0);

  /** Add a place; `stay` — keep picking (Shift+click). */
  const addTarget = useCallback((next: Target, stay = false) => {
    setTargets((list) => withTarget(list, next));
    setSelection(null);
    window.getSelection()?.removeAllRanges();
    if (!stay) {
      setMode('ask');
      focusInput();
    }
  }, []);

  const removeTarget = (id: number) => setTargets((list) => list.filter((item) => item.id !== id));
  // The panel is open from the start: leaving the pick goes back to it, places or not.
  const backFromPick = useCallback(() => {
    setHover(null);
    setMode((current) => (current === 'idle' ? current : 'ask'));
    focusInput();
  }, []);

  const startQuestion = () => {
    if (!askLive) return;
    closeMenu();
    // Зв2: a fresh opening is a plain chat; the place of the previous opening does not follow.
    if (mode !== 'ask') setTargets([]);
    setMode('ask');
    focusInput();
  };

  // Esc closes only the topmost layer (facts model widget_ui, П306): the pick goes back to the panel; in the question
  // field it closes the panel — only while the field has focus, as before, so the host page keeps its own Esc. The menu
  // is a library Popover: a click outside and Esc close it.
  useEscapeLayer(mode === 'pick', backFromPick);
  useEscapeLayer(mode === 'ask' && inputFocused, () => setMode('idle'));
  useEffect(() => {
    if (mode !== 'ask') setInputFocused(false);
  }, [mode]);

  // Pick: hover outlines an element, click adds it, Shift+click — and stays.
  useEffect(() => {
    if (mode !== 'pick') return;
    const pickable = (node: EventTarget | null): Element | null => {
      if (!(node instanceof Element) || node.closest(OWN)) return null;
      return node.closest(`[${attr}]`) ?? node;
    };
    const onMove = (event: PointerEvent) => setHover(pickable(event.target));
    const onClick = (event: MouseEvent) => {
      const el = pickable(event.target);
      if (!el) return;
      event.preventDefault();
      event.stopPropagation();
      setHover(null);
      addTarget(elementTarget(attr, el), event.shiftKey);
    };
    const block = (event: Event) => {
      if (event.target instanceof Element && !event.target.closest(OWN)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    document.addEventListener('pointermove', onMove, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('mousedown', block, true);
    document.addEventListener('pointerdown', block, true);
    pickCursor(true);
    return () => {
      document.removeEventListener('pointermove', onMove, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('mousedown', block, true);
      document.removeEventListener('pointerdown', block, true);
      pickCursor(false);
    };
  }, [mode, attr, addTarget]);

  // Text selected on the page — "ask" pops up at its start ("to the question" while the panel is open).
  useEffect(() => {
    if (!ask || mode === 'pick' || mode === 'area') return;
    const check = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setSelection(null);
        return;
      }
      const selected = sel.toString();
      const range = sel.getRangeAt(0);
      const node = range.commonAncestorContainer;
      const el = node instanceof Element ? node : node.parentElement;
      if (!el || el.closest(OWN) || el.closest('input, textarea, [contenteditable="true"]') || squash(selected, 400).length < 2) {
        setSelection(null);
        return;
      }
      const rect = range.getClientRects()[0] ?? range.getBoundingClientRect();
      setSelection({
        target: { id: newTargetId(), kind: 'text', anchors: anchorsOf(attr, el), text: squash(selected, 300), el },
        x: Math.max(8, Math.min(rect.left, window.innerWidth - 140)),
        y: Math.max(8, rect.top - 36),
      });
    };
    const onUp = (event: Event) => {
      if (event.target instanceof Element && event.target.closest(OWN)) return;
      window.setTimeout(check, 0);
    };
    const hide = () => setSelection(null);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('keyup', onUp);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keyup', onUp);
      window.removeEventListener('scroll', hide, true);
    };
  }, [ask, mode, attr]);

  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [turns]);

  /**
   * The answered questions of this chat before `upTo` (all when absent), as the host takes them; the latest version of each.
   * The whole chat goes, held only by the ceiling in characters (Чт1, Quint ask_chat); `ask.history` turns it on, the number
   * no longer cuts. After «Новый чат» (Чт2) the chat is empty, so is the history.
   */
  const historyOf = useCallback(
    (list: Turn[], upTo?: number) => {
      if (!(Number(ask?.history) > 0)) return null;
      const before = upTo === undefined ? list : list.slice(0, Math.max(0, list.findIndex((turn) => turn.id === upTo)));
      return fitHistory(
        before
          .filter((turn) => turn.state === 'done' && turn.answer)
          .map((turn) => ({ question: turn.question, answer: (turn.answer!.found ? turn.answer!.answer : turn.answer!.guess) ?? '' })),
      );
    },
    [ask],
  );

  /** Чт2: a new chat — no turns, so no history; the place and the unsent text go too (the window as opened). */
  const newChat = () => {
    setTurns([]);
    setTargets([]);
    setText('');
    inputRef.current?.focus();
  };

  const request = useCallback(
    (question: string, places: Target[], depth: AnswerDepth, history: ReturnType<typeof historyOf>) => {
      const options = {
        ...(depthOn ? { depth } : {}),
        ...(audienceOn ? { audience } : {}),
        ...(history ? { history } : {}),
        hints: ask!.hints === true,
        // Тв7а: no place picked — the service answers from the screen's visible blocks.
        ...(viaService && places.length === 0 ? { screen: visibleMarks(attr) } : {}),
      };
      return viaService ? askService(question, places, options) : askCode(ask!.path ?? '', question, places, options);
    },
    [ask, viaService, depthOn, audienceOn, audience, attr],
  );

  const send = useCallback(async () => {
    const question = text.trim();
    if (!question || !ask || askOff) return;
    const id = nextTurn.current++;
    const asked = targets;
    const history = historyOf(turns);
    const places: QuestionPlace[] = asked.map((target) => ({
      kind: target.kind,
      anchors: target.anchors,
      text: target.kind === 'screen' ? '' : redactText(target.text),
    }));
    setTurns((list) => [...list, { id, question, target: placesLabel(asked), targets: asked, places, depth: 'simple', state: 'wait' }]);
    setText('');
    const patch = (change: Partial<Turn>) => setTurns((list) => list.map((turn) => (turn.id === id ? { ...turn, ...change } : turn)));
    let answer: CodeAnswer;
    try {
      answer = await request(question, asked, 'simple', history);
    } catch (err) {
      patch({ state: 'error', error: err instanceof ApiError ? err.message : 'Не получилось — повторите' });
      return;
    }
    // The service stored it while answering: nothing to send again.
    if (answer.id !== undefined) {
      patch({ state: 'done', answer, saved: { state: 'saved', id: answer.id } });
      return;
    }
    // A host's own answer goes to the tracker from here; without the service it stays in the panel only.
    patch({ state: 'done', answer, ...(serviceOn ? { saved: { state: 'saving' as const } } : {}) });
    if (!serviceOn) return;
    try {
      const { question: stored } = await bugApi.saveQuestion({
        question: redactText(question),
        places,
        route: hostRoute(),
        pageUrl: window.location.href,
        answer: { found: answer.found, answer: answer.answer, guess: answer.guess, proofs: answer.proofs },
      });
      patch({ saved: { state: 'saved', id: stored.id } });
    } catch {
      patch({ saved: { state: 'failed' } });
    }
  }, [ask, askOff, text, targets, turns, historyOf, request, serviceOn]);

  /** The same question at another depth, with the questions before it; the new answer takes the old one's place. */
  const redo = useCallback(
    async (turn: Turn, depth: AnswerDepth) => {
      if (!ask || turn.state !== 'done') return;
      const history = historyOf(turns, turn.id);
      setTurns((list) => list.map((item) => (item.id === turn.id ? { ...item, redo: depth, redoError: undefined } : item)));
      try {
        const answer = await request(turn.question, turn.targets, depth, history);
        // The service stores every answer: the new one is the row the panel points to.
        const saved = answer.id !== undefined ? { saved: { state: 'saved' as const, id: answer.id } } : {};
        setTurns((list) => list.map((item) => (item.id === turn.id ? { ...item, answer, depth, redo: undefined, ...saved } : item)));
      } catch (err) {
        const error = err instanceof ApiError ? err.message : 'Не получилось — повторите';
        setTurns((list) => list.map((item) => (item.id === turn.id ? { ...item, redo: undefined, redoError: error } : item)));
      }
    },
    [ask, turns, historyOf, request],
  );
  const asking = turns.some((turn) => turn.state === 'wait' || !!turn.redo);

  const onKeyDown = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void send();
    }
  };

  const wholeScreen = () => {
    setHover(null);
    addTarget(screenTarget(attr));
  };

  // The report panel is open full height on the right: the button hides; a minimized draft raises it.
  if (draft && !minimized) return null;
  const busy = uxWatching || draft;
  const numbered = targets.length > 1;
  const picking = mode === 'pick' || mode === 'area';
  const raised = minimized ? ' help-raised' : '';

  // The help button: floating in the corner, or a quiet button of the host's frame (П610) — one of them, never both.
  const launcherButton = (variant: 'filled' | 'quiet', place: string) => (
    <>
      <IconButton
        variant={variant}
        className={`help-launcher${place}`}
        label="Помощь"
        icon={<ChatIcon />}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        onClick={(event) => {
          const anchor = event.currentTarget;
          onMenuOpen?.();
          setMenuAnchor((open) => (open ? null : anchor));
        }}
      />
      {/* A draft or a recording: a dot on the button's corner (the library's `busy` would disable the button). */}
      {busy && <span className={`help-launcher-dot${place}`} aria-hidden />}
    </>
  );

  const hostCall = (run: () => void) => () => {
    try {
      run();
    } catch {
      // The host callback must not break the widget.
    }
  };
  const menuItems: MenuEntry[] = [
    ...(ask
      ? [{ key: 'ask', label: 'Задать вопрос', icon: <HelpIcon />, disabled: !askLive, tip: askLive ? undefined : OFF_TIP, onSelect: startQuestion }]
      : []),
    ...(showBugs
      ? [
          {
            key: 'bug',
            label: draft ? 'Вернуться к баг-репорту' : 'Отправить баг-репорт',
            icon: <BugIcon />,
            disabled: !bugsOn,
            tip: bugsOn ? undefined : OFF_TIP,
            onSelect: onBugReport,
          },
        ]
      : []),
    // «Последи за мной» on its own switch in the tracker (widget_features П315).
    ...(uxWatchOffered()
      ? [
          {
            key: 'ux',
            label: uxWatching ? 'Идёт запись' : 'Последи за мной',
            icon: <EyeIcon />,
            disabled: !serviceOn || uxWatching,
            tip: serviceOn ? undefined : OFF_TIP,
            onSelect: onUxWatch,
          },
        ]
      : []),
    ...(screenMaskOffered
      ? [
          {
            kind: 'toggle' as const,
            key: 'mask',
            label: 'Скрыть данные людей на экране',
            icon: <ShieldIcon />,
            checked: screenMasked,
            tip: 'ФИО, ники, Telegram ID, телефоны, заметки и переписка на странице размыты, заголовки, кнопки и счётчики открыты — для показа экрана · Alt+Shift+M',
            onChange: () => toggleScreenMask(),
          },
        ]
      : []),
    ...(openWhatsNew ? [{ key: 'news', label: 'Что нового', icon: <SparkIcon />, onSelect: hostCall(openWhatsNew) }] : []),
    // П317: on in the tracker, the site has no page for it — grey with the reason.
    ...(!openWhatsNew && whatsNewWithoutPage()
      ? [{ key: 'news', label: 'Что нового', icon: <SparkIcon />, disabled: true, tip: 'Сайт не подключил страницу «Что нового»', onSelect: () => {} }]
      : []),
    ...(showTracker ? [{ key: 'tracker', label: isSuper ? 'Репорты и вопросы' : 'Мои вопросы', icon: <ListIcon />, onSelect: hostCall(openTracker!) }] : []),
  ];

  return (
    <div className="help-root" {...OWN_PROPS}>
      {picking && (
        <div className="help-pickbar">
          {mode === 'pick' ? <ClickIcon /> : <CropIcon />}
          <span>{mode === 'pick' ? 'Щёлкните · Shift — ещё' : 'Обведите область'}</span>
          {targets.length > 0 && <Counter value={targets.length} tone="accent" strong label="Выбрано мест" />}
          {mode === 'pick' ? (
            <Button size="sm" variant="quiet" icon={<CropIcon />} disabled={full} onClick={() => setMode('area')}>
              Область
            </Button>
          ) : (
            <Button size="sm" variant="quiet" icon={<ClickIcon />} onClick={() => setMode('pick')}>
              Элемент
            </Button>
          )}
          <Button size="sm" variant="quiet" icon={<ScreensIcon />} onClick={wholeScreen}>
            Весь экран
          </Button>
          <Button size="sm" variant="secondary" onClick={backFromPick}>
            Готово
          </Button>
        </div>
      )}
      {mode === 'pick' && <Outline el={hover} tone="pick" />}
      {mode === 'area' && <AreaFrame onDone={(box) => addTarget(areaTarget(attr, box))} onCancel={backFromPick} />}

      {mode !== 'idle' &&
        targets.map((target, index) =>
          target.kind === 'screen' ? null : (
            <Outline key={target.id} el={target.el} box={target.box} tone="chosen" label={numbered ? String(index + 1) : undefined} />
          ),
        )}

      {selection && !picking && (
        <div className="help-selask" style={{ left: selection.x, top: selection.y }}>
          {mode === 'ask' ? (
            <Button size="sm" variant="primary" icon={<PlusIcon />} disabled={full} onMouseDown={(event) => event.preventDefault()} onClick={() => addTarget(selection.target)}>
              К вопросу
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              icon={<HelpIcon />}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                setTargets([]);
                addTarget(selection.target);
              }}
            >
              Спросить
            </Button>
          )}
        </div>
      )}

      {mode === 'ask' && ask && (
        <section className={`help-panel${raised}`} aria-label="Вопрос по экрану">
          <PanelHead
            className="help-panel-head"
            title={
              <>
                <HelpIcon />
                Вопрос по экрану
              </>
            }
            end={
              <>
                {turns.length > 0 && (
                  <Button size="sm" variant="quiet" icon={<PlusIcon />} title="Начать вопросы заново: прежний разговор не учитывается" onClick={newChat}>
                    Новый чат
                  </Button>
                )}
                <IconButton size="sm" label="Закрыть" icon={<CloseIcon />} onClick={() => setMode('idle')} />
              </>
            }
          />
          <div className="help-thread" ref={threadRef}>
            {turns.map((turn) => (
              <div key={turn.id} className="help-turn">
                <div className="help-q" {...PANEL_TEXT}>
                  <span className="help-q-target">{turn.target}</span>
                  <span className="help-q-text">{turn.question}</span>
                </div>
                <AnswerView
                  turn={turn}
                  depth={{ on: depthOn, busy: asking, onPick: (depth) => void redo(turn, depth) }}
                  onReport={
                    bugsOn && onReportFromQuestion && turn.state === 'done'
                      ? () => {
                          // Чт3: the report carries every answered turn of this chat, in the order they were asked.
                          const seeds = turns
                            .map((item) => seedOf(item))
                            .filter((seed): seed is QuestionSeed => seed !== null);
                          if (seeds.length === 0) return;
                          setMode('idle');
                          onReportFromQuestion(seeds);
                        }
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
          <div className="help-compose">
            <div className="help-targets">
              {targets.map((target, index) => {
                const Icon = TARGET_ICON[target.kind];
                return (
                  <Chip
                    key={target.id}
                    className="help-target"
                    icon={<Icon />}
                    title={target.kind === 'screen' ? 'Весь экран' : target.text || 'Выбранное место'}
                    onRemove={() => removeTarget(target.id)}
                    removeLabel="Убрать место"
                  >
                    <span {...PANEL_TEXT}>
                      {numbered ? `${index + 1} · ` : ''}
                      {excerpt(target)}
                    </span>
                  </Chip>
                );
              })}
              <Button size="sm" variant="quiet" icon={<PlusIcon />} disabled={full} onClick={() => setMode('pick')}>
                Место
              </Button>
            </div>
            {audienceOn && (
              <div className="help-modes" role="group" aria-label="Вид ответа">
                <Chip
                  variant="outline"
                  icon={audience === 'product' ? <UsersIcon /> : <FileCodeIcon />}
                  title={AUDIENCE[audience].tip}
                  onClick={() => setAudience(AUDIENCE[audience].next)}
                >
                  {AUDIENCE[audience].text}
                </Chip>
              </div>
            )}
            {askOff && (
              <Banner tone="info" className="help-off">
                {askOff}
              </Banner>
            )}
            <div className="help-input-row">
              <TextArea
                ref={inputRef}
                className="help-input"
                {...PANEL_TEXT}
                label="Ваш вопрос"
                hideLabel
                maxRows={6}
                maxLength={2000}
                placeholder={askOff ? 'Ответов сейчас не будет' : 'Ваш вопрос'}
                disabled={!!askOff}
                value={text}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={onKeyDown}
                onFocus={() => setInputFocused(true)}
                onBlur={() => setInputFocused(false)}
              />
              <IconButton variant="primary" label="Спросить · Enter" icon={<SendIcon />} disabled={!text.trim() || !!askOff} onClick={() => void send()} />
            </div>
          </div>
        </section>
      )}

      {slotRoot
        ? createPortal(
            <span className="help-bar-btn" {...OWN_PROPS}>
              {launcherButton('quiet', '')}
            </span>,
            slotRoot,
          )
        : launcherButton('filled', raised)}
      {/* Report 179 (Quint facts report_panel П343): no beacon by the button — the mode is a checked item of this menu and
          the shield in the tools row of the report window (report 190). */}
      {/* Library menu, rendered inside the widget root (data-jui-portal), so it stays the widget's own. Off items keep
          focus and the reason in the tip (widget_controls П310); the screen mode is a toggle and keeps the menu open. */}
      <Menu anchor={menuAnchor} onClose={closeMenu} label="Помощь" className="help-menu" items={menuItems} />
    </div>
  );
}
